import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trophy, Medal, Lock, ChevronLeft, ChevronRight, RefreshCw, Info } from "lucide-react";
import { leaderboardContext, leaderboard, errorMessage } from "./api";
import { card, field, th, td, Bar } from "./ui";

// The Leaderboard page. Which board a person sees is decided by the server from their own account: this page only shows
// what the server offers (the scope list in /leaderboard/context) and never sends a school, class or role of its own
// making. Students and individuals get one fixed scope; teachers and principals get a dropdown.

const PAGE_SIZE = 25;
const MEDAL = ["text-gold-500", "text-slate-400", "text-terracotta-600"];   // gold, silver, bronze

function RankCell({ rank }) {
  if (rank <= 3) {
    const Icon = rank === 1 ? Trophy : Medal;
    return <span className="inline-flex items-center gap-1.5 font-semibold"><Icon className={`h-4 w-4 ${MEDAL[rank - 1]}`} aria-hidden /><span data-testid={`rank-${rank}`}>{rank}</span></span>;
  }
  return <span className="font-semibold text-foreground/70">{rank}</span>;
}

function Message({ tone = "info", title, children, testid }) {
  return (
    <div className={`${card} text-center py-10`} data-testid={testid} role={tone === "error" ? "alert" : "status"}>
      <div className="mx-auto h-10 w-10 grid place-items-center rounded-full bg-forest-600/10 text-forest-700 mb-3">{tone === "locked" ? <Lock className="h-5 w-5" /> : <Info className="h-5 w-5" />}</div>
      <div className="font-display font-semibold text-forest-700">{title}</div>
      <div className="text-sm text-foreground/70 mt-1 max-w-md mx-auto">{children}</div>
    </div>
  );
}

export default function Leaderboard({ token }) {
  const [ctx, setCtx] = useState(null);
  const [ctxErr, setCtxErr] = useState("");
  const [scope, setScope] = useState(null);
  const [app, setApp] = useState("overall");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const req = useRef(0);   // only the newest request may update the page

  const loadCtx = useCallback(() => {
    setCtxErr("");
    leaderboardContext(token).then((c) => { setCtx(c); setScope((s) => s || c.default_scope); })
      .catch((e) => setCtxErr(errorMessage(e, "We couldn't load the leaderboard")));
  }, [token]);
  useEffect(() => { loadCtx(); }, [loadCtx]);

  const load = useCallback(() => {
    if (!ctx || ctx.state !== "ok" || !scope) return;
    const n = ++req.current;
    setLoading(true); setErr("");
    leaderboard(token, { scope, app, page, page_size: PAGE_SIZE })
      .then((d) => { if (n === req.current) setData(d); })
      .catch((e) => { if (n === req.current) setErr(errorMessage(e, "We couldn't load this leaderboard")); })
      .finally(() => { if (n === req.current) setLoading(false); });
  }, [token, ctx, scope, app, page]);
  useEffect(() => { load(); }, [load]);

  const header = (
    <div>
      <h1 className="font-display font-semibold text-2xl text-forest-700 inline-flex items-center gap-2"><Trophy className="h-6 w-6 text-gold-500" aria-hidden />Leaderboard</h1>
      <p className="text-sm text-foreground/70 mt-1">Track your progress and see where you stand.</p>
    </div>
  );

  if (ctxErr) return <div className="space-y-5" data-testid="leaderboard-page">{header}<Message tone="error" title="Leaderboard unavailable" testid="lb-error">{ctxErr} <button type="button" className="underline" onClick={loadCtx}>Try again</button></Message></div>;
  if (!ctx) return <div className="space-y-5" data-testid="leaderboard-page">{header}<div className="text-forest-700" data-testid="lb-loading">Loading…</div></div>;
  if (ctx.state !== "ok") return <div className="space-y-5" data-testid="leaderboard-page">{header}<Message title="No leaderboard to show yet" testid="lb-unavailable">{ctx.message}</Message></div>;

  const scopeLabel = (ctx.scopes.find((s) => s.id === scope) || {}).label || "";
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(total, page * PAGE_SIZE);
  const me = data?.me;
  const appName = (ctx.apps.find((a) => a.id === app) || {}).name || "";
  const staff = ctx.role === "teacher" || ctx.role === "principal";

  return (
    <div className="space-y-5" data-testid="leaderboard-page">
      {header}

      <div className="flex flex-wrap gap-4 items-end" data-testid="lb-filters">
        <label className="block min-w-[220px] flex-1 max-w-xs">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Scope</span>
          {ctx.fixed || ctx.scopes.length === 1 ? (
            <div className={`${field} bg-sand-100 inline-flex items-center gap-2`} data-testid="lb-scope-fixed"><Lock className="h-3.5 w-3.5 text-foreground/50" aria-hidden />{scopeLabel}</div>
          ) : (
            <select className={field} value={scope} data-testid="lb-scope" onChange={(e) => { setScope(e.target.value); setPage(1); }}>
              {ctx.scopes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          )}
        </label>
        <label className="block min-w-[220px] flex-1 max-w-xs">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Application</span>
          <select className={field} value={app} data-testid="lb-app" onChange={(e) => { setApp(e.target.value); setPage(1); }}>
            {ctx.apps.map((a) => <option key={a.id} value={a.id} disabled={a.locked}>{a.name}{a.locked ? " (locked)" : ""}</option>)}
          </select>
        </label>
        {loading && <span className="text-xs text-foreground/60 pb-3" data-testid="lb-updating">Updating…</span>}
      </div>

      {!staff && me && (
        <div className={`${card} flex flex-wrap items-center gap-x-8 gap-y-3 bg-forest-600/5`} data-testid="lb-me">
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Your rank</div>
            <div className="font-display font-semibold text-3xl text-forest-700" data-testid="lb-me-rank">{me.rank ? `#${me.rank}` : "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Your score</div>
            <div className="font-display font-semibold text-3xl text-forest-700" data-testid="lb-me-score">{me.score}</div></div>
          <div className="text-sm text-foreground/70">
            {me.rank ? <>Out of {me.of} ranked in <strong>{scopeLabel}</strong> · {appName}</> : "No activity recorded yet. Your rank appears as soon as you start learning."}
          </div>
        </div>
      )}

      {err ? <Message tone="error" title="Couldn't load this leaderboard" testid="lb-error">{err} <button type="button" className="underline inline-flex items-center gap-1" onClick={load}><RefreshCw className="h-3 w-3" />Retry</button></Message>
        : !data ? <div className="text-forest-700" data-testid="lb-loading">Loading…</div>
        : data.state === "locked" ? <Message tone="locked" title="Not included in your plan" testid="lb-locked">{data.message}</Message>
        : data.state === "unsupported" ? <Message title="Not ranked yet" testid="lb-unsupported">{data.message}</Message>
        : data.state === "no_class" || data.state === "no_classes" || data.state === "no_school" ? <Message title="No leaderboard to show yet" testid="lb-unavailable">{data.message}</Message>
        : data.state === "empty" ? <Message title="Nobody to rank yet" testid="lb-empty">{data.message}</Message>
        : (
          <div className={`${card} p-0 overflow-hidden ${loading ? "opacity-70" : ""}`} data-testid="lb-table-card">
            <div className="px-5 pt-4 pb-3 flex flex-wrap items-baseline justify-between gap-2">
              <div className="font-display font-semibold text-forest-700">{scopeLabel} · {appName}</div>
              <div className="text-xs text-foreground/60" data-testid="lb-total">{total} ranked</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="lb-table">
                <thead><tr><th className={`${th} w-20`}>Rank</th><th className={th}>{staff ? "Student" : "Name"}</th>{data.show_class && <th className={th}>Class</th>}<th className={`${th} w-48`}>Score</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => {
                    const mine = r.user_id === data.me_id;
                    return (
                      <tr key={r.user_id} data-testid={`lb-row-${r.rank}`} className={mine ? "bg-forest-600/10" : r.rank <= 3 ? "bg-gold-500/5" : ""} aria-current={mine ? "true" : undefined}>
                        <td className={td}><RankCell rank={r.rank} /></td>
                        <td className={`${td} font-medium`}>{r.name}{mine && <span className="ml-2 text-[10px] font-bold tracking-wider rounded-full bg-forest-700 text-sand-100 px-2 py-0.5">YOU</span>}
                          {r.partial && <span className="ml-2 text-[10px] text-amber-800 bg-gold-500/15 rounded-full px-2 py-0.5" title="One of this person's apps couldn't be read, so it is left out of their overall score">partial</span>}</td>
                        {data.show_class && <td className={td}>{r.class_name} {r.section}</td>}
                        <td className={td}><div className="flex items-center gap-3"><span className="w-12 font-semibold tabular-nums">{r.score}</span><div className="flex-1 min-w-[60px]"><Bar percent={r.score} /></div></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-sand-300 flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-foreground/60" data-testid="lb-range">Showing {from}–{to} of {total}</span>
              <div className="inline-flex items-center gap-2">
                <button type="button" className="btn-ghost !py-1.5 !px-3 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="lb-prev" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></button>
                <span className="tabular-nums" data-testid="lb-page">Page {page} of {pages}</span>
                <button type="button" className="btn-ghost !py-1.5 !px-3 disabled:opacity-40" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="lb-next" aria-label="Next page"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        )}

      <div className="text-xs text-foreground/60 space-y-1" data-testid="lb-footnote">
        <p>{ctx.scoring}</p>
        {ctx.unsupported?.length > 0 && <p>Not ranked yet: {ctx.unsupported.map((u) => `${u.name} (${u.reason})`).join(" ")}</p>}
      </div>
    </div>
  );
}
